// src/home/hub.client.ts — HUB DE HĂRȚI (#/harti): pagina din care staff-ul alege o hartă și intră în
// spațiul ei de lucru. Înlocuiește selectorul persistent din topbar (decizia coordonatorului): topbar-ul
// nu mai are dropdown de hărți + buton MCP; alegerea/comutarea hărții se face de aici. Click pe card →
// set_workspace + navighează la #/map. Linkul MCP (CU token, gata de lipit în ChatGPT) trăiește tot aici,
// pe fiecare card, doar pentru staff/admin.

import van from "vanjs-core"
import { navigate } from "../lib/router.client"
import { copy_to_clipboard, mcp_url, set_workspace, workspace } from "../lib/workspace.client"
import { api_get } from "../lib/api.client"
import { session } from "../lib/session.client"

const t = van.tags

interface MapCard {
	slug: string
	name: string
	description: string | null
	org_id: string | null
	org_name: string
}

interface MapGroup {
	key: string
	name: string
	maps: MapCard[]
}

/**
 * Grupează hărțile pe organizație (primărie). Ordine: org-urile UTILIZATORULUI întâi (ordinea din
 * memberships), apoi restul org-urilor (public, alfabetic), apoi „Neasignate / publice" (org_id NULL).
 * Pentru super/support (fără memberships) grupează pe numele org-ului fiecărei hărți.
 */
function build_groups(maps: MapCard[]): MapGroup[] {
	const by_org = new Map<string, MapGroup>()
	for (const m of maps) {
		const key = m.org_id ?? "__none__"
		let g = by_org.get(key)
		if (!g) {
			g = {
				key,
				name: key === "__none__" ? "Neasignate / publice" : m.org_name || m.slug,
				maps: [],
			}
			by_org.set(key, g)
		}
		g.maps.push(m)
	}
	const my_org_ids = session.val.memberships.map((mb) => mb.org_id)
	const ordered: MapGroup[] = []
	const used = new Set<string>()
	for (const oid of my_org_ids) {
		const g = by_org.get(oid)
		if (g && !used.has(oid)) {
			ordered.push(g)
			used.add(oid)
		}
	}
	const rest = [...by_org.values()].filter((g) => !used.has(g.key) && g.key !== "__none__")
	rest.sort((a, b) => a.name.localeCompare(b.name, "ro"))
	ordered.push(...rest)
	const none = by_org.get("__none__")
	if (none) {
		ordered.push(none)
	}
	return ordered
}

const hub_maps = van.state<MapCard[] | null>(null) // null = încă se încarcă

async function load_hub_maps() {
	try {
		const res = await fetch("/api/maps", { credentials: "include" })
		const body = (await res.json()) as { ok: boolean; data?: MapCard[] }
		hub_maps.val = body.ok && Array.isArray(body.data) ? body.data : []
	} catch {
		hub_maps.val = []
	}
}

/** Staff = user autentificat cu drepturi de back-office (membru al unui org sau super/support). */
function is_staff(): boolean {
	const s = session.val
	return (
		s.authenticated &&
		(s.memberships.length > 0 ||
			s.user?.platform_role === "super" ||
			s.user?.platform_role === "support")
	)
}

const MCP_IDLE = "⧉ Link AI (MCP)"

/** Un card de hartă: nume + descriere + „Deschide" (whole-card) + link MCP tokenizat (staff/admin). */
function HubCard(m: MapCard) {
	const active = () => workspace.val.slug === m.slug
	const mcp_label = van.state(MCP_IDLE)
	const flash = (s: string, ms = 2000) => {
		mcp_label.val = s
		setTimeout(() => (mcp_label.val = MCP_IDLE), ms)
	}
	return t.div(
		{
			class: () =>
				"text-left rounded-2xl border p-5 transition hover:shadow-md hover:-translate-y-0.5 cursor-pointer " +
				(active()
					? "border-cyan-600 bg-cyan-50/60 dark:bg-cyan-950/40 ring-1 ring-cyan-600/30"
					: "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"),
			onclick: () => {
				set_workspace(m.slug, m.name)
				navigate("/map")
			},
		},
		t.div(
			{ class: "flex items-center justify-between gap-2" },
			t.h3(
				{ class: "font-bold text-slate-900 dark:text-slate-100 truncate", title: m.name || m.slug },
				m.name || m.slug,
			),
			() =>
				active()
					? t.span(
							{
								class:
									"flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cyan-700 text-white",
							},
							"activă",
						)
					: t.span(
							{
								class:
									"flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400",
							},
							"deschide →",
						),
		),
		m.description
			? t.p(
					{ class: "mt-2 text-sm text-slate-600 dark:text-slate-300 line-clamp-2" },
					m.description,
				)
			: t.p({ class: "mt-2 text-sm text-slate-400 dark:text-slate-500 font-mono" }, m.slug),
		// Link MCP (CU token) — doar staff/admin; stopPropagation ca butonul să NU deschidă harta.
		t.button(
			{
				class:
					"mt-4 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 " +
					"bg-slate-50 dark:bg-slate-950 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 " +
					"hover:text-slate-900 transition cursor-pointer",
				style: () => (is_staff() ? "" : "display:none"),
				title: "Copiază linkul MCP complet (cu token) al hărții — gata de lipit în ChatGPT",
				onclick: async (e: Event) => {
					e.stopPropagation()
					mcp_label.val = "…"
					const r = await api_get<{ token: string | null; org_id: string | null }>(
						`/api/maps/${encodeURIComponent(m.slug)}/mcp-token`,
					)
					if (!r.ok) {
						flash(r.err?.code === "forbidden" ? "Doar adminii org-ului" : "Eroare")
						return
					}
					if (!r.data.token) {
						flash(r.data.org_id ? "Eroare token" : "Harta nu e asignată unui org")
						return
					}
					const ok = await copy_to_clipboard(mcp_url(m.slug, r.data.token))
					flash(ok ? "Link copiat ✓" : "Eroare")
				},
			},
			() => mcp_label.val,
		),
	)
}

/** Filtrează hărțile după numele/slug-ul hărții sau numele primăriei (căutare). */
function match_query(m: MapCard, q: string): boolean {
	return (
		(m.name || m.slug).toLowerCase().includes(q) ||
		m.slug.toLowerCase().includes(q) ||
		m.org_name.toLowerCase().includes(q)
	)
}

/** Pagina hub de hărți (#/harti). Header-ul e furnizat de shell (app.client). */
export function MapsHubPage(): HTMLElement {
	queueMicrotask(load_hub_maps)
	const query = van.state("")
	return t.div(
		{ class: "min-h-[calc(100vh-3rem)] bg-slate-50 dark:bg-slate-950" },
		t.div(
			{ class: "max-w-6xl mx-auto px-6 py-10" },
			t.h1(
				{ class: "text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100" },
				"Spații de lucru",
			),
			t.p(
				{ class: "mt-1 text-slate-600 dark:text-slate-300" },
				"Alege o hartă pentru a intra în spațiul ei de lucru (hartă, documente, registre).",
			),
			// Căutare — vizibilă doar când există hărți.
			t.input({
				type: "text",
				placeholder: "Caută o hartă (nume sau primărie)…",
				class:
					"mt-6 w-full max-w-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm " +
					"text-slate-800 dark:text-slate-200 outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 transition",
				style: () => ((hub_maps.val?.length ?? 0) > 0 ? "" : "display:none"),
				oninput: (e: Event) => (query.val = (e.target as HTMLInputElement).value),
			}),
			() => {
				const all = hub_maps.val
				if (all === null) {
					return t.p(
						{ class: "mt-8 text-sm text-slate-400 dark:text-slate-500 animate-pulse" },
						"Se încarcă hărțile…",
					)
				}
				if (all.length === 0) {
					return t.div(
						{
							class:
								"mt-8 rounded-2xl border border-dashed border-slate-300 dark:border-slate-600 p-10 text-center",
						},
						t.p({ class: "text-slate-500 dark:text-slate-400" }, "Nicio hartă disponibilă încă."),
						t.a(
							{
								href: "#/administrare",
								class:
									"inline-block mt-4 px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-medium hover:bg-cyan-800",
							},
							"Importă o hartă din Administrare",
						),
					)
				}
				const q = query.val.trim().toLowerCase()
				const maps = q ? all.filter((m) => match_query(m, q)) : all
				if (maps.length === 0) {
					return t.p(
						{ class: "mt-8 text-sm text-slate-400 dark:text-slate-500" },
						`Nicio hartă nu se potrivește cu „${query.val}".`,
					)
				}
				// Grupate pe primărie (org): o secțiune per organizație + „Neasignate / publice".
				return t.div(
					{ class: "mt-8 space-y-8" },
					...build_groups(maps).map((g) =>
						t.section(
							{},
							t.h2(
								{
									class:
										"text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3",
								},
								g.name,
							),
							t.div(
								{ class: "grid sm:grid-cols-2 lg:grid-cols-3 gap-4" },
								...g.maps.map((m) => HubCard(m)),
							),
						),
					),
				)
			},
		),
	)
}

// Lista de hărți s-a schimbat (import/creare/ștergere/reasignare) → reîmprospătează hub-ul.
if (typeof window !== "undefined") {
	window.addEventListener("maps:changed", () => {
		void load_hub_maps()
	})
}
